import { ReactNode, ReactElement } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { defineMessages, useIntl } from 'dibs-react-intl';

import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { PhoneNumberInput } from 'dibs-phone-number-input/exports/PhoneNumber';

import {
    RECEIVE_SMS_UPDATES,
    RECEIVE_WHATS_APP_UPDATES,
    SMS_PHONE_NUMBER,
    WHATS_APP_PHONE_NUMBER,
    FormData,
} from '../reducer';

import styles from './styles/IFramedPromoPartnershipPCForm.scss';

type FieldsetType = 'sms' | 'whatsApp';
type CheckboxField = typeof RECEIVE_SMS_UPDATES | typeof RECEIVE_WHATS_APP_UPDATES;
type InputField = typeof SMS_PHONE_NUMBER | typeof WHATS_APP_PHONE_NUMBER;

const messages = defineMessages({
    smsLabel: {
        id: 'iFramedpromoPartnership.SmsWhatsAppFieldSet.smsLabel',
        defaultMessage: 'Would you like to receive SMS updates from your Private Client Advisor?',
    },
    smsPlaceholder: {
        id: 'iFramedpromoPartnership.SmsWhatsAppFieldSet.smsPlaceholder',
        defaultMessage: 'Phone number for SMS updates',
    },
    whatsAppLabel: {
        id: 'iFramedpromoPartnership.SmsWhatsAppFieldSet.whatsAppLabel',
        defaultMessage:
            'Would you like to receive WhatsApp updates from your Private Client Advisor?',
    },
    whatsAppPlaceholder: {
        id: 'iFramedpromoPartnership.SmsWhatsAppFieldSet.whatsAppPlaceholder',
        defaultMessage: 'Phone number for WhatsApp updates',
    },
});

type Props<T extends FieldsetType> = {
    type: T;
    checkboxValue: FormData[CheckboxField];
    inputValue: FormData[InputField];
    errorMessage: ReactNode;
    disabled?: boolean;
    setField: <U extends CheckboxField | InputField>(field: U, value: FormData[U]) => void;
};

const SmsWhatsAppFieldSet = <T extends FieldsetType>({
    type,
    checkboxValue: isChecked,
    inputValue,
    errorMessage,
    disabled,
    setField,
}: Props<T>): ReactElement => {
    const { formatMessage } = useIntl();
    const environment = useRelayEnvironment();

    return (
        <fieldset className={styles.fieldset}>
            <div className={isChecked ? styles.relatedField : styles.field}>
                <Checkbox
                    dataTn={`promo-partnership-${type}-checkbox`}
                    name={`promo-partnership-${type}`}
                    label={formatMessage(messages[`${type}Label`])}
                    checked={isChecked}
                    onChange={checked =>
                        setField(
                            type === 'sms' ? RECEIVE_SMS_UPDATES : RECEIVE_WHATS_APP_UPDATES,
                            checked
                        )
                    }
                />
            </div>
            {isChecked && (
                <div className={styles.field}>
                    <PhoneNumberInput
                        dataTn={`promo-partnership-${type}-input`}
                        name={`promo-partnership-${type}`}
                        flagWrapperPosition="top"
                        placeholder={formatMessage(messages[`${type}Placeholder`])}
                        environment={environment}
                        phoneNumber={inputValue}
                        onChange={({ value, isValid }) =>
                            setField(
                                type === 'sms' ? SMS_PHONE_NUMBER : WHATS_APP_PHONE_NUMBER,
                                isValid ? value : ''
                            )
                        }
                        disabled={disabled}
                        errorMessage={errorMessage}
                    />
                </div>
            )}
        </fieldset>
    );
};

export default SmsWhatsAppFieldSet;
